import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authService } from "../services/auth.service";
import { tokenService } from "../api/token.service";
import { setOnSessionExpired } from "../api/client";

const AuthContext = createContext(null);

/**
 * AuthProvider
 *
 * Pure state container for "who is logged in right now" -nothing else.
 * No API calls for login/signup/logout live here; those are mutations,
 * and mutations belong in useAuthMutations() (React Query), same pattern
 * as useRecipients(). This file's only jobs are:
 *
 *  1. Hold `user` / `loading` state and expose it via useAuth().
 *  2. On mount, restore the session from a stored token by calling /me.
 *  3. Expose setUser/clearUser so useAuthMutations() can sync this state
 *     after a successful login/signup/logout - it calls the service,
 *     this just reflects the result.
 *  4. Subscribe to client.js's onSessionExpired callback, so a failed
 *     token refresh anywhere in the app drops the UI back to logged-out.
 *
 * Wrap your app once, near the root (above the router):
 *   <AuthProvider><BrowserRouter><App /></BrowserRouter></AuthProvider>
 */
export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((nextUser) => setUserState(nextUser), []);
  const clearUser = useCallback(() => setUserState(null), []);

  useEffect(() => {
    // client.js calls this when a refresh attempt fails outright, so the
    // UI drops back to "logged out" instead of looping on 401s.
    setOnSessionExpired(() => {
      tokenService.clearTokens();
      clearUser();
    });
  }, [clearUser]);

  useEffect(() => {
    async function restoreSession() {
      if (!tokenService.getAccessToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await authService.getMe();
        setUserState(me);
      } catch {
        tokenService.clearTokens();
        setUserState(null);
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, []);

  const value = {
    user,
    isAuthenticated: Boolean(user),
    loading,
    setUser,
    clearUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** useAuth() - read auth state (user, isAuthenticated, loading) from anywhere in the tree. */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}